import { useEffect, useState, useRef } from "react";
import LogoIcon from "@/assets/LogoIcon";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [atTop, setAtTop] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const resourcesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setAtTop(window.scrollY === 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resourcesRef.current &&
        !resourcesRef.current.contains(event.target as Node)
      ) {
        setIsResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <motion.header
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`sticky top-0 z-50 transition-all duration-300 backdrop-blur-sm ${
        atTop ? "bg-white border-none" : `bg-white/75 border-zinc-300 ${isMobileMenuOpen ? "" : "border-b"}`
      }`}
    >
      <div className="max-w-5xl mx-auto py-4 px-6 lg:px-0 flex justify-between items-center">
        <Link
          to="/"
          className="text-2xl font-semibold text-zinc-900 flex items-center gap-2"
        >
          <LogoIcon size={18} color="#eab308" />
          Talos
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden sm:flex items-center gap-6">
          {/* Resources Dropdown */}
          <div className="relative" ref={resourcesRef}>
            <button
              onClick={() => setIsResourcesOpen(!isResourcesOpen)}
              className="inline-flex items-center gap-1 text-md text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              Resources
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${
                  isResourcesOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isResourcesOpen && (
              <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-lg border border-zinc-200 shadow-lg py-1 z-50">
                <Link
                  to="/resources/payer-directory"
                  className="block px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                  onClick={() => setIsResourcesOpen(false)}
                >
                  Payer Directory
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/demo"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2 rounded-lg text-lg font-medium hover:bg-zinc-800 transition-colors"
          >
            Book a Demo
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="sm:hidden p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-zinc-900" />
          ) : (
            <Menu className="h-6 w-6 text-zinc-900" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="sm:hidden border rounded-lg border-zinc-200 bg-white"
        >
          <div className="p-4 flex flex-col gap-4">
            <Link
              to="/blog"
              className="text-lg text-zinc-900 hover:text-zinc-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Blog
            </Link>
            {/* Mobile Resources Section */}
            <div>
              <button
                onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
                className="flex items-center gap-1 text-lg text-zinc-900 hover:text-zinc-900 transition-colors w-full"
              >
                Resources
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isMobileResourcesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isMobileResourcesOpen && (
                <div className="ml-4 mt-2 flex flex-col gap-2">
                  <Link
                    to="/resources/payer-directory"
                    className="text-md text-zinc-600 hover:text-zinc-900 transition-colors"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsMobileResourcesOpen(false);
                    }}
                  >
                    Payer Directory
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/demo"
              className="inline-flex items-center justify-center gap-2 bg-zinc-900 text-white px-5 py-2 rounded-lg text-lg font-medium hover:bg-zinc-800 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Book a Demo
            </Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;
